import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';

/**
 * Fix applications that are missing businessId by looking up the job's businessId
 */
export const fixMissingBusinessId = async () => {
  try {
    console.log('🔧 Starting to fix applications missing businessId...');
    
    // Get all applications
    const applicationsSnapshot = await getDocs(collection(firestore, 'jobApplications'));
    const allApplications = applicationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📋 Found ${allApplications.length} total applications`);
    
    // Find applications missing businessId
    const missingBusinessId = allApplications.filter(app => !app.businessId);
    console.log(`🔍 Found ${missingBusinessId.length} applications missing businessId:`, missingBusinessId);
    
    if (missingBusinessId.length === 0) {
      console.log('✅ No applications missing businessId found!');
      return {
        total: allApplications.length,
        missing: 0,
        fixed: 0
      };
    }
    
    // Fix each application by looking up the job's businessId
    let fixedCount = 0;
    for (const app of missingBusinessId) {
      try {
        console.log(`🔧 Fixing application ${app.id} for job ${app.jobId}...`);
        
        if (!app.jobId) {
          console.warn(`  ⚠️ Application ${app.id} has no jobId, skipping`);
          continue;
        }
        
        // Look up the job to get businessId
        const jobDoc = await getDoc(doc(firestore, 'jobs', app.jobId));
        
        if (jobDoc.exists()) {
          const jobData = jobDoc.data();
          if (jobData.businessId) {
            // Update the application with the businessId
            await updateDoc(doc(firestore, 'jobApplications', app.id), {
              businessId: jobData.businessId,
              updatedAt: new Date(),
              fixedAt: new Date() // Add a timestamp to track when this was fixed
            });
            
            console.log(`  ✅ Added businessId ${jobData.businessId} to application ${app.id}`);
            fixedCount++;
          } else {
            console.warn(`  ⚠️ Job ${app.jobId} has no businessId`);
          }
        } else {
          console.warn(`  ⚠️ Job ${app.jobId} not found`);
        }
        
      } catch (error) {
        console.error(`  ❌ Error fixing application ${app.id}:`, error);
      }
    }
    
    console.log(`🎉 Fixed ${fixedCount} applications successfully!`);
    
    return {
      total: allApplications.length,
      missing: missingBusinessId.length,
      fixed: fixedCount
    };
    
  } catch (error) {
    console.error('❌ Error in fix process:', error);
    throw error;
  }
};

/**
 * Fix jobs that are missing businessId by looking up the business owner
 */
export const fixMissingJobBusinessId = async () => {
  try {
    console.log('🔧 Starting to fix jobs missing businessId...');
    
    // Get all jobs
    const jobsSnapshot = await getDocs(collection(firestore, 'jobs'));
    const allJobs = jobsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📋 Found ${allJobs.length} total jobs`);
    
    // Find jobs missing businessId
    const missingBusinessId = allJobs.filter(job => !job.businessId);
    console.log(`🔍 Found ${missingBusinessId.length} jobs missing businessId:`, missingBusinessId);
    
    if (missingBusinessId.length === 0) {
      console.log('✅ No jobs missing businessId found!');
      return {
        total: allJobs.length,
        missing: 0,
        fixed: 0
      };
    }
    
    // Get all users to find business owners
    const usersSnapshot = await getDocs(collection(firestore, 'users'));
    const businessOwners = {};
    
    usersSnapshot.docs.forEach(doc => {
      const userData = doc.data();
      if (userData.businessId && userData.role === 'business') {
        businessOwners[doc.id] = userData.businessId;
      }
    });
    
    console.log(`📊 Found ${Object.keys(businessOwners).length} business owners`);
    
    let fixedCount = 0;
    for (const job of missingBusinessId) {
      try {
        console.log(`🔧 Attempting to fix job ${job.id} (${job.title})...`);
        
        // Strategy 1: Check if job has a manager/creator field
        let businessId = null;
        
        if (job.createdBy && businessOwners[job.createdBy]) {
          businessId = businessOwners[job.createdBy];
          console.log(`  📝 Found businessId via createdBy: ${businessId}`);
        }
        
        // Strategy 2: Look for business address match
        if (!businessId && job.address) {
          const businessesSnapshot = await getDocs(collection(firestore, 'businesses'));
          
          for (const businessDoc of businessesSnapshot.docs) {
            const businessData = businessDoc.data();
            if (businessData.address && job.address) {
              // Check if addresses match
              const addressMatch = (
                businessData.address.city === job.address.city &&
                businessData.address.state === job.address.state &&
                businessData.address.street === job.address.street
              );
              
              if (addressMatch) {
                businessId = businessDoc.id;
                console.log(`  🏠 Found businessId via address match: ${businessId}`);
                break;
              }
            }
          }
        }
        
        // Update the job if we found a businessId
        if (businessId) {
          await updateDoc(doc(firestore, 'jobs', job.id), {
            businessId: businessId,
            updatedAt: new Date(),
            fixedAt: new Date()
          });
          
          console.log(`  ✅ Added businessId ${businessId} to job ${job.id}`);
          fixedCount++;
        } else {
          console.warn(`  ⚠️ Could not determine businessId for job ${job.id}`);
        }
        
      } catch (error) {
        console.error(`  ❌ Error fixing job ${job.id}:`, error);
      }
    }
    
    console.log(`🎉 Fixed ${fixedCount} jobs successfully!`);
    
    return {
      total: allJobs.length,
      missing: missingBusinessId.length,
      fixed: fixedCount
    };
    
  } catch (error) {
    console.error('❌ Error in fix process:', error);
    throw error;
  }
};

/**
 * Comprehensive fix for both jobs and applications
 */
export const fixAllMissingBusinessIds = async () => {
  try {
    console.log('🚀 Starting comprehensive businessId fix...');
    
    // Step 1: Fix jobs first
    console.log('\n📝 Step 1: Fixing jobs...');
    const jobResults = await fixMissingJobBusinessId();
    
    // Step 2: Fix applications
    console.log('\n📋 Step 2: Fixing applications...');
    const appResults = await fixMissingBusinessId();
    
    console.log('\n🎯 SUMMARY:');
    console.log(`Jobs: ${jobResults.fixed}/${jobResults.missing} fixed`);
    console.log(`Applications: ${appResults.fixed}/${appResults.missing} fixed`);
    
    return {
      jobs: jobResults,
      applications: appResults
    };
    
  } catch (error) {
    console.error('❌ Error in comprehensive fix:', error);
    throw error;
  }
}; 